import React, { useState, useRef, useEffect } from 'react';
import { readSessionToken, readVerifiedEmail, clearSession } from '../contexts/AuthContext';
import { generateCSRFToken } from '../utils/security';
import { validateSessionLocally } from '../hooks/useSessionGuard';
import { X, Check, Shield, Zap, BookOpen, Cpu, FileText, Users, AlertCircle, RefreshCw, Smartphone } from 'lucide-react';

// Detect iOS PWA standalone mode — Razorpay popup is blocked here.
// In standalone mode we show a warning and open Razorpay in a new tab instead.
const isIOSStandalone = () =>
  typeof window !== 'undefined' &&
  window.navigator.standalone === true;

const isAndroidPWA = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(display-mode: standalone)').matches &&
  /android/i.test(navigator.userAgent);

// ─── Razorpay SDK lazy loader ─────────────────────────────────────────────────
const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement('script');
    s.src     = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload  = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const SUPPORT_ITEMS = [
  { icon: BookOpen, text: 'More QA guides & cheatsheets' },
  { icon: Cpu,      text: 'AI-powered testing tools' },
  { icon: Users,    text: 'Keep most content free' },
  { icon: FileText, text: 'Interview prep & career roadmaps' },
];

const BENEFITS = [
  'All premium PDFs, templates & cheatsheets',
  'Early access to new resources',
  'Support an independent QA community',
  'Cancel anytime, no questions asked',
];

// ─── Success screen ───────────────────────────────────────────────────────────
const SuccessScreen = ({ onClose }) => (
  <div className="flex flex-col items-center justify-center py-8 px-6 text-center animate-fade-in">
    <div className="w-16 h-16 bg-gradient-to-br from-[#DCFCE7] to-[#BBF7D0] rounded-2xl flex items-center justify-center mb-4 shadow-sm">
      <Check className="w-8 h-8 text-[#15803D]" strokeWidth={2.5} />
    </div>
    <div className="text-2xl mb-1">🎉</div>
    <h3 className="text-lg font-bold text-[#0F172A] mb-1">Premium Activated!</h3>
    <p className="text-sm text-[#64748B] mb-5 leading-relaxed">
      Thank you for supporting TestingVala ❤️<br />
      Your access is now active.
    </p>
    <div className="w-full bg-[#F0FDF4] border border-[#BBF7D0] rounded-xl px-4 py-3 mb-5 text-left">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-[#15803D] uppercase tracking-wide">Active Plan</span>
        <span className="text-xs font-bold text-[#FF6600] bg-[#FFF7ED] px-2 py-0.5 rounded-full">₹9/mo</span>
      </div>
      <p className="text-sm font-semibold text-[#0F172A]">Insight Library Premium</p>
      <p className="text-xs text-[#64748B] mt-0.5">⚡ Instant access enabled</p>
    </div>
    <button
      onClick={onClose}
      className="w-full bg-[#0057B7] hover:bg-[#004494] active:scale-[0.98] text-white text-sm font-semibold py-3 rounded-xl transition-all duration-150 shadow-sm"
    >
      Start Downloading →
    </button>
  </div>
);

// ─── Main PremiumModal ────────────────────────────────────────────────────────
const PremiumModal = ({ isOpen, onClose, onSuccess, userEmail, sessionToken, pendingItem, onSessionExpired }) => {
  const [phase,       setPhase]       = useState('info');
  const [errorMsg,    setErrorMsg]    = useState('');
  const [slowPayment, setSlowPayment] = useState(false);
  const paymentTimeoutRef = useRef(null);
  const slowTimerRef      = useRef(null);

  // Phase 5: Validate session the moment the modal tries to open.
  // If invalid — never render the modal, redirect to home instead.
  useEffect(() => {
    if (!isOpen) return;
    const { valid, reason } = validateSessionLocally();
    if (!valid) {
      clearSession();
      onClose();
      onSessionExpired?.();
      return;
    }
    // Reset state for fresh open
    setPhase('info');
    setErrorMsg('');
    setSlowPayment(false);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Cleanup timeout on unmount ────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (paymentTimeoutRef.current) clearTimeout(paymentTimeoutRef.current);
      if (slowTimerRef.current)      clearTimeout(slowTimerRef.current);
    };
  }, []);

  if (!isOpen) return null;

  const clearPaymentTimeout = () => {
    if (paymentTimeoutRef.current) { clearTimeout(paymentTimeoutRef.current); paymentTimeoutRef.current = null; }
    if (slowTimerRef.current)      { clearTimeout(slowTimerRef.current);      slowTimerRef.current      = null; }
    setSlowPayment(false);
  };

  const handleSubscribe = async () => {
    setPhase('paying');
    setErrorMsg('');

    // Always read fresh from localStorage — props may be stale if auth state
    // changed after the component rendered.
    const freshToken = readSessionToken() || sessionToken;
    const freshEmail = readVerifiedEmail() || userEmail;

    if (!freshToken || !freshEmail) {
      setErrorMsg('Session expired — please sign in again.');
      setPhase('error');
      return;
    }

    // Generate a fresh CSRF token for this payment attempt
    const csrfToken = generateCSRFToken();

    try {
      // 1. Load Razorpay SDK
      const loaded = await loadRazorpay();
      if (!loaded) throw new Error('Payment service unavailable. Please check your connection and try again.');

      // 2. Create order server-side — CSRF token required in production
      const orderRes = await fetch('/api/payment?action=order', {
        method:  'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': csrfToken,
        },
        body: JSON.stringify({ token: freshToken, email: freshEmail }),
      });

      if (!orderRes.ok) {
        const err = await orderRes.json().catch(() => ({}));
        if (orderRes.status === 403) throw new Error('Security check failed. Please refresh the page and try again.');
        if (orderRes.status === 401) {
          // Stale session — clear it so the user is prompted to re-login
          clearSession();
          onClose();
          onSessionExpired?.();
          throw new Error('session_expired_reauth');
        }
        throw new Error(err.error || 'Failed to create payment order. Please try again.');
      }

      const { orderId, amount, currency, keyId } = await orderRes.json();

      // 3. Open Razorpay checkout with 2-minute timeout guard.
      // On iOS PWA standalone, window.open() is blocked by Safari.
      // Razorpay internally uses window.open for its checkout iframe/popup.
      // We detect this and set config accordingly so Razorpay uses
      // its redirect flow instead of popup on iOS standalone.
      const iosStandalone = isIOSStandalone();
      await new Promise((resolve, reject) => {
        let settled = false;
        const safeResolve = () => { if (!settled) { settled = true; resolve(); } };
        const safeReject  = (e) => { if (!settled) { settled = true; reject(e); } };

        paymentTimeoutRef.current = setTimeout(() => {
          safeReject(new Error('Payment timed out. Please try again.'));
        }, 180000); // 3 minutes

        // After 30s show a "taking longer" hint without blocking the flow
        slowTimerRef.current = setTimeout(() => setSlowPayment(true), 30000);

        const rzp = new window.Razorpay({
          key:         keyId,
          amount,
          currency,
          order_id:    orderId,
          name:        'TestingVala',
          description: 'Insight Library Premium — ₹9/month',
          image:       '/testingvala-logo.png',
          prefill:     { email: freshEmail, contact: '' },
          theme:       { color: '#0057B7' },
          // Explicitly enable all payment methods — UPI shown first
          method: {
            upi:        true,
            card:       true,
            netbanking: true,
            wallet:     true,
          },
          config: {
            display: {
              blocks: {
                upi: {
                  name: 'Pay using UPI / QR Code',
                  instruments: [
                    { method: 'upi', flows: ['intent', 'qr', 'omnichannel'] }
                  ]
                },
              },
              sequence: ['block.upi', 'block.card', 'block.netbanking', 'block.wallet'],
              preferences: { show_default_blocks: true },
            },
          },
          // On iOS PWA: use redirect mode so checkout doesn’t need window.open
          // On iOS PWA: redirect to /payment-success (GET-compatible landing page)
          ...(iosStandalone ? { redirect: true, callback_url: `${window.location.origin}/payment-success` } : {}),
          modal: {
            ondismiss: () => {
              clearPaymentTimeout();
              safeReject(new Error('cancelled'));
            },
          },
          handler: async (response) => {
            settled = true;
            clearPaymentTimeout();
            try {
              // 4. Verify payment server-side — fresh CSRF token for verify call
              const verifyCsrf = generateCSRFToken();
              const verifyRes = await fetch('/api/payment?action=verify', {
                method:  'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'x-csrf-token': verifyCsrf,
                },
                body: JSON.stringify({
                  token:               freshToken,
                  email:               freshEmail,
                  razorpay_order_id:   response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature:  response.razorpay_signature,
                }),
              });

              if (!verifyRes.ok) {
                const err = await verifyRes.json().catch(() => ({}));
                if (verifyRes.status === 403) throw new Error('Security check failed during verification. Please contact support.');
                throw new Error(err.error || 'Payment verification failed. Please contact support if amount was deducted.');
              }

              resolve();
            } catch (e) {
              reject(e);
            }
          },
        });
        rzp.open();
      });

      // 5. Success — server confirmed activation
      setPhase('success');
      // Dispatch event so any other mounted components (e.g. header badge)
      // can react without prop-drilling
      window.dispatchEvent(new CustomEvent('premium-activated', { detail: { email: freshEmail } }));
      try {
        const bc = new BroadcastChannel('tv-auth-sync');
        bc.postMessage({ type: 'PREMIUM_ACTIVATED', email: freshEmail });
        bc.close();
      } catch (_) {}
      onSuccess?.();

    } catch (err) {
      clearPaymentTimeout();
      if (err.message === 'cancelled') {
        setPhase('info');
      } else if (err.message === 'session_expired_reauth') {
        // Already handled above (session cleared, modal closed, re-auth triggered)
      } else {
        setErrorMsg(err.message || 'Something went wrong. Please try again.');
        setPhase('error');
      }
    }
  };

  const handleRetry = () => {
    setPhase('info');
    setErrorMsg('');
  };

  const handleClose = () => {
    clearPaymentTimeout();
    // Don't reset phase here — useEffect on isOpen handles it on next open
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[99999] flex items-end sm:items-center justify-center p-0 sm:p-4"
         style={{ background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(4px)' }}>
      <div className="bg-white w-full sm:max-w-[420px] rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[95dvh] overflow-y-auto border border-white/60"
           style={{ animation: 'modalIn 0.22s cubic-bezier(0.34,1.56,0.64,1)' }}>

        <style>{`
          @keyframes modalIn {
            from { opacity: 0; transform: translateY(12px) scale(0.97); }
            to   { opacity: 1; transform: translateY(0)    scale(1);    }
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(6px); }
            to   { opacity: 1; transform: translateY(0);   }
          }
          .animate-fade-in { animation: fadeIn 0.3s ease-out; }
        `}</style>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3.5 border-b border-[#F1F5F9]">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-gradient-to-br from-[#FFF7ED] to-[#FFEDD5] border border-[#FED7AA] rounded-xl flex items-center justify-center shadow-sm">
              <Zap className="w-4 h-4 text-[#FF6600]" />
            </div>
            <div>
              <h2 className="text-[15px] font-bold text-[#0F172A] leading-tight tracking-tight">
                Unlock Premium Access
              </h2>
              <p className="text-[11px] text-[#94A3B8] mt-0.5">Support the TestingVala community</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-7 h-7 flex items-center justify-center text-[#94A3B8] hover:text-[#475569] hover:bg-[#F1F5F9] rounded-lg transition-all duration-150"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        {phase === 'success' ? (
          <SuccessScreen onClose={handleClose} />
        ) : (
          <div className="px-5 pt-4 pb-5 space-y-4">

            {/* Pending resource context */}
            {pendingItem && (
              <div className="flex items-center gap-2.5 bg-[#EFF6FF] border border-[#BFDBFE] rounded-xl px-3 py-2.5">
                <FileText className="w-4 h-4 text-[#0057B7] flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-[11px] text-[#3B82F6] font-medium">Trying to access</p>
                  <p className="text-sm font-semibold text-[#0F172A] truncate leading-tight">{pendingItem.title}</p>
                </div>
              </div>
            )}

            {/* Pricing hero */}
            <div className="bg-gradient-to-br from-[#F8FAFC] to-[#EFF6FF] border border-[#E2E8F0] rounded-2xl p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-extrabold text-[#0F172A] tracking-tight">₹9</span>
                    <span className="text-sm text-[#94A3B8] font-medium">/month</span>
                  </div>
                  <p className="text-xs text-[#64748B] mt-0.5">Less than a cup of tea ☕</p>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <div className="inline-flex items-center gap-1 bg-[#DCFCE7] text-[#15803D] text-[11px] font-bold px-2 py-1 rounded-full">
                    <span>⚡</span> Instant access
                  </div>
                  <div className="inline-flex items-center gap-1 bg-white border border-[#E2E8F0] text-[#64748B] text-[11px] font-medium px-2 py-1 rounded-full">
                    <Shield className="w-2.5 h-2.5" /> Secure
                  </div>
                </div>
              </div>
            </div>

            {/* Two-column layout: support + benefits */}
            <div className="grid grid-cols-2 gap-3">
              {/* Your ₹9 helps */}
              <div className="bg-[#F8FAFC] rounded-xl p-3 border border-[#F1F5F9]">
                <p className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider mb-2">Your ₹9 helps</p>
                <div className="space-y-1.5">
                  {SUPPORT_ITEMS.map(({ icon: Icon, text }) => (
                    <div key={text} className="flex items-start gap-1.5">
                      <Icon className="w-3 h-3 text-[#0057B7] flex-shrink-0 mt-0.5" />
                      <span className="text-[11px] text-[#475569] leading-tight">{text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* What you get */}
              <div className="bg-[#F0FDF4] rounded-xl p-3 border border-[#DCFCE7]">
                <p className="text-[10px] font-bold text-[#15803D] uppercase tracking-wider mb-2">You get</p>
                <div className="space-y-1.5">
                  {BENEFITS.map((b) => (
                    <div key={b} className="flex items-start gap-1.5">
                      <Check className="w-3 h-3 text-[#15803D] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                      <span className="text-[11px] text-[#475569] leading-tight">{b}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Error state */}
            {phase === 'error' && errorMsg && (
              <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMsg}</p>
              </div>
            )}

            {/* CTAs */}
            <div className="space-y-2 pt-0.5">
              {phase === 'error' ? (
                <div className="flex gap-2">
                  <button
                    onClick={handleRetry}
                    className="flex-1 flex items-center justify-center gap-2 bg-[#0057B7] hover:bg-[#004494] active:scale-[0.98] text-white text-sm font-semibold py-3 rounded-xl transition-all duration-150 shadow-sm"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Try Again
                  </button>
                  <button
                    onClick={handleClose}
                    className="flex-1 text-sm text-[#64748B] hover:text-[#0F172A] border border-[#E2E8F0] hover:border-[#CBD5E1] py-3 rounded-xl transition-all duration-150"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <>
                  <button
                    onClick={handleSubscribe}
                    disabled={phase === 'paying'}
                    className="w-full flex items-center justify-center gap-2 bg-[#0057B7] hover:bg-[#004494] active:scale-[0.98] text-white text-[15px] font-bold py-3.5 rounded-xl transition-all duration-150 shadow-md disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
                  >
                    {phase === 'paying' ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        <span>Opening payment...</span>
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4" />
                        <span>Get Premium Access — ₹9/month</span>
                      </>
                    )}
                  </button>
                  <p className="text-center text-[11px] text-[#94A3B8]">
                    Secure payments powered by Razorpay
                  </p>
                  {slowPayment && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 text-center">
                      <p className="text-xs font-semibold text-amber-800 mb-1">Payment taking longer than expected?</p>
                      <p className="text-[11px] text-amber-700 mb-2">Check if a payment window opened behind this screen.</p>
                      <div className="flex gap-2">
                        <button onClick={handleRetry} className="flex-1 text-xs font-semibold text-[#0057B7] border border-[#BFDBFE] bg-[#EFF6FF] py-1.5 rounded-lg transition-colors">Retry</button>
                        <button onClick={handleClose} className="flex-1 text-xs text-[#64748B] border border-[#E2E8F0] py-1.5 rounded-lg transition-colors">Cancel</button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {phase !== 'error' && (
                <button
                  onClick={handleClose}
                  disabled={phase === 'paying'}
                  className="w-full text-xs text-[#94A3B8] hover:text-[#64748B] py-1.5 transition-colors duration-150 disabled:opacity-40"
                >
                  Maybe later
                </button>
              )}
            </div>

            {/* Trust strip */}
            <div className="flex items-center justify-center gap-4 pt-0.5 pb-1">
              {['⚡ Instant access', '🔒 No hidden charges', '↩ Cancel anytime'].map((t) => (
                <span key={t} className="text-[10px] text-[#94A3B8] font-medium whitespace-nowrap">{t}</span>
              ))}
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default PremiumModal;
